import { createEvent } from "library/local-events/index.mjs"
function getSymbolKey(symbol) {
    return `@@${symbol.toString()}`
}
const MIN_DICTIONARY_STRING_LENGTH = 16
const DefinedSymbol = createEvent("DefinedSymbol", {
    extract: (v) => v[0].listOfSymbols,
})
function registerSymbol(symbol) {
    if (typeof symbol === "string") {
        symbol = Symbol(symbol)
    }
    DefinedSymbol.handleOnce(({ listOfSymbols }) => {
        listOfSymbols.push(symbol)
    })
    return symbol
}
function deserializeCircular(jsonString, listOfSymbols = []) {
    listOfSymbols = DefinedSymbol.call({
        listOfSymbols,
    })
    const symbolLookup = Object.fromEntries(listOfSymbols.map((s) => [getSymbolKey(s), s]))
    const objects = {}
    const fixups = []
    let stringDict = null
    let result = jsonString
    try {
        result = JSON.parse(jsonString, revive)
        fixups.forEach((f) => f())
    } catch (e) {
        console.error(e)
        throw e
    }

    // Unwrap if wrapped
    if (result && typeof result === "object" && result.$$_wrap !== undefined) {
        result = result.$$_wrap
    }
    return result
    function revive(key, value) {
        // Capture and remove the string dictionary
        if (key === "$$_strings" && Array.isArray(value)) {
            stringDict = value
            return undefined
        }

        // Replace string dictionary references
        if (typeof value === "object" && value && value.$$_s !== undefined) {
            const idx = value.$$_s
            if (stringDict) {
                return stringDict[idx]
            }
            const target = this
            const prop = key
            fixups.push(() => {
                target[prop] = stringDict ? stringDict[idx] : undefined
            })
            return null
        }
        if (typeof value === "object" && value?.$$_id !== undefined) {
            objects[value.$$_id] = value
            delete value.$$_id
        }

        // Handle prototype restoration
        if (key === "$proto" && value) {
            const target = this
            fixups.push(() => {
                const protoValue = value?.startsWith?.("$$") ? objects[value.slice(2)] : value
                if (protoValue && typeof protoValue === "object") {
                    Object.setPrototypeOf(target, protoValue)
                }
            })
            return undefined
        }
        if (typeof key === "string" && key.startsWith("@@")) {
            const target = this
            fixups.push(() => {
                value = value?.startsWith?.("$$") ? objects[value.slice(2)] : value
                if (symbolLookup[key]) {
                    target[symbolLookup[key]] = value
                }
            })
            return undefined
        }
        if (!value) return value
        if (value.startsWith?.("$$")) {
            const target = this
            fixups.push(() => {
                target[key] = objects[value.slice(2)]
            })
            return null
        }
        return value
    }
}
function serializeCircular(object) {
    const fixup = []
    const seen = new WeakSet()
    try {
        let counter = 0
        recurseConvertSymbols(object)
        const circular = new WeakMap()

        // Prepare string dictionary and wrapper
        const strings = []
        const stringToIndex = new Map()
        const wrapper = {
            $$_wrap: object,
            $$_strings: strings,
        }
        return JSON.stringify(wrapper, replace)

        // eslint-disable-next-line no-inner-declarations
        function replace(key, value) {
            // Do not intern strings inside the dictionary itself
            if (typeof value === "string") {
                if (this && this.__isStringsDict) return value
                if (value.length > MIN_DICTIONARY_STRING_LENGTH) {
                    let idx = stringToIndex.get(value)
                    if (idx === undefined) {
                        idx = strings.length
                        strings.push(value)
                        stringToIndex.set(value, idx)
                    }
                    return {
                        $$_s: idx,
                    }
                }
                return value
            }
            if (!Array.isArray(value) && typeof value === "object" && value) {
                if (!circular.has(value)) {
                    circular.set(value, counter)
                    const result = {
                        $$_id: counter++,
                    }
                    let k
                    try {
                        for (k of Object.keys(value)) {
                            result[k] = value[k]
                        }
                    } catch (e) {
                        console.error("Error cloning value:", e, value, k)
                        console.log(value[k])
                        throw e
                    }

                    // Check if the prototype is not an empty object
                    const proto = Object.getPrototypeOf(value)
                    if (proto && proto !== Object.prototype && proto !== Array.prototype) {
                        // Check if proto has any properties
                        const protoKeys = Object.getOwnPropertyNames(proto)
                        const protoSymbols = Object.getOwnPropertySymbols(proto)
                        if (protoKeys.length > 0 || protoSymbols.length > 0) {
                            // Recursively serialize the prototype
                            if (circular.has(proto)) {
                                result.$proto = `$$${circular.get(proto)}`
                            } else {
                                // Let the prototype be processed in the next iteration
                                result.$proto = proto
                            }
                        }
                    }
                    value = result
                } else {
                    value = `$$${circular.get(value)}`
                }
            } else if (Array.isArray(value)) {
                const cloned = [...value]
                if (key === "$$_strings") {
                    Object.defineProperty(cloned, "__isStringsDict", {
                        value: true,
                        enumerable: false,
                    })
                }
                return cloned
            }
            return value
        }
    } finally {
        fixup.forEach((f) => f())
    }
    function recurseConvertSymbols(object) {
        if (!object) return
        if (typeof object !== "object") return
        if (seen.has(object)) return
        seen.add(object)
        if (Array.isArray(object)) {
            object.forEach(recurseConvertSymbols)
        } else {
            Object.getOwnPropertySymbols(object).forEach((key) => {
                const value = object[key]
                const symbolKey = getSymbolKey(key)
                const hadSymbolKey = Object.prototype.hasOwnProperty.call(object, symbolKey)
                const existingSymbolKeyValue = object[symbolKey]
                let wroteSymbolKey = false
                try {
                    object[symbolKey] = value
                    wroteSymbolKey = true
                } catch (e) {
                    // If we cannot materialize symbol keys on this object, skip that symbol.
                    // This can happen for non-extensible objects.
                }

                if (wroteSymbolKey) {
                    fixup.push(() => {
                        if (hadSymbolKey) {
                            object[symbolKey] = existingSymbolKeyValue
                        } else {
                            delete object[symbolKey]
                        }
                    })
                }
                recurseConvertSymbols(value)
            })
            Object.keys(object).forEach((key) => recurseConvertSymbols(object[key]))
        }
    }
}
export { serializeCircular }
export { deserializeCircular }
export { registerSymbol }
